import React, { useCallback, useEffect, useMemo, useState } from 'react';

import classnames from 'classnames';

import LinkButton, { TextButton } from 'components/uiLibrary/LinkButton';
import SpriteIcon from 'components/uiLibrary/SpriteIcon';
import Tooltip from 'components/uiLibrary/Tooltip';
import Typography from 'components/uiLibrary/Typography';

import useTracking from 'components/Globals/Analytics';
import {
  CATEGORIES,
  COMPONENTS,
  GOOGLE_OLD_TRACKING_SERVICES,
  SUB_COMPONENTS,
} from 'components/Globals/Analytics/constants';
import ComposerWorkField from 'components/Globals/FormFields/ComposerWorkField';
import ProductionCountryField from 'components/Globals/FormFields/ProductionCountryField';
import SinceYearField from 'components/Globals/FormFields/SinceYearField';
import WorkRoleField from 'components/Globals/FormFields/WorkRoleField/WorkRoleField';
import OperatorBlock from 'components/Globals/Layout/Header/GlobalSearch/OperatorBlock/OperatorBlock';
import { useShowPageAccessPortalForCasting } from 'components/Globals/PortalsProvider';

import {
  BOOLEAN_SEARCH_PARTS,
  CASTING_TOOL_SLUGS,
  CASTING_TOOL_TABS,
  DATE_FORMATS,
  DEFAULT_BOOLEAN_QUERY,
  DEFAULT_OPERATOR,
  EVENTS,
  TP,
  URL_SLUG_TYPES,
} from 'constants/index';

import { cleanBooleanQuery, getBooleanSearchQuery } from 'utils/casting';
import { createDate } from 'utils/date';
import { generateFilterParams } from 'utils/globals/filters';
import useAppContext from 'utils/hooks/useAppContext';
import usePageAccessContext from 'utils/hooks/usePageAccessContext';
import usePageContext from 'utils/hooks/usePageContext';
import { trackClearForm, trackSearch, trackSearchFilter, trackSearchGo } from 'utils/tracking';

import { Trans, useTranslation } from 'src/i18n';

import { CASTING_RESULT_TYPES_VALUES, FILTER_TYPES, ROUTE_RESERVED_KEYWORDS } from 'operabase-router/constants';

import classes from './DesktopSearch.module.scss';

const YearCountryFilter = ({ sinceYear, onChangeHandler, workId, countries, filters, trackingData }) => {
  const { t } = useTranslation('NS_CASTING_TOOL');
  const track = useTracking();
  return (
    <>
      <SinceYearField
        value={sinceYear}
        onChange={selectedYear => {
          onChangeHandler(URL_SLUG_TYPES.SINCE_YEAR, selectedYear);
          track.click(trackSearchFilter(URL_SLUG_TYPES.SINCE_YEAR, selectedYear), GOOGLE_OLD_TRACKING_SERVICES);
        }}
        trackingData={{ category: CATEGORIES.SEARCH, subComponent: SUB_COMPONENTS.YEAR, ...trackingData }}
        disabled={!workId}
        queryParams={{
          ...(filters?.[URL_SLUG_TYPES.COUNTRY] || {}),
          work_id: workId,
        }}
        styles={{ root: classes.sinceYearSelect }}
      />
      <div className={classes.yearCountry}>
        <Typography size={12} weight="medium" className={classes.input__title}>
          {t(`${TP}.m_COUNTRY`)}
          <Tooltip title={t(`${TP}.PRODUCTION_COUNTRY_TOOLTIP_TITLE`)}>
            <span>
              <SpriteIcon icon="info_outline" size={14} className={classes.input__title_questionIcon} />
            </span>
          </Tooltip>
        </Typography>
        <ProductionCountryField
          countries={countries}
          prefixNote={t(`${TP}.FN_CASTING_TOOLTIP`)}
          onChange={selectedLocations => {
            onChangeHandler(FILTER_TYPES.COUNTRY, selectedLocations);
            track.click(trackSearchFilter(URL_SLUG_TYPES.COUNTRY, selectedLocations), GOOGLE_OLD_TRACKING_SERVICES);
          }}
          disabled={!workId}
          styles={{
            select: classes.country__select,
          }}
          trackingData={{ category: CATEGORIES.SEARCH, subComponent: SUB_COMPONENTS.COUNTRY, ...trackingData }}
          queryParams={{
            ...(sinceYear && {
              date_from: createDate(`${sinceYear}`).format(DATE_FORMATS.FULL_DATE),
            }),
            work_id: workId,
          }}
          customPopperWidth={225}
          workId={workId}
          showLabel={false}
        />
      </div>
    </>
  );
};

const Actions = ({ onClearSearch, onSearch, workId, trackingData, multipleRows, isRoleDebut, roleDebutWorkRoleId }) => {
  const { t } = useTranslation('NS_CASTING_TOOL');

  return (
    <div className={classnames(classes.buttonContainer, { [classes.buttonContainer_multipleRows]: multipleRows })}>
      <TextButton
        leftIcon={<SpriteIcon icon="replay-bold" size="16" className={classes.clearIcon} />}
        variant="tertiary"
        onClick={onClearSearch}
        trackingData={{ component: COMPONENTS.CLEAR_CTA, ...trackingData }}
      />
      <LinkButton
        variant="orange"
        onClick={onSearch}
        disabled={isRoleDebut ? !roleDebutWorkRoleId || !workId : !workId}
        leftIcon={<SpriteIcon icon="search" size="16" />}
        trackingData={{ component: COMPONENTS.SUBMIT_CTA, ...trackingData }}
      >
        <Typography size="12" weight="medium" className={classes.searchButton}>
          {t(`${TP}.SEARCH`)}
        </Typography>
      </LinkButton>
    </div>
  );
};

const DesktopSearch = ({ trackingData = {}, activeTab }) => {
  const { t } = useTranslation('NS_CASTING_TOOL');
  const track = useTracking();
  const { obRouteContext } = useAppContext();
  const { applyFilters } = usePageContext();
  const showPageAccessPortalForCasting = useShowPageAccessPortalForCasting();
  const { activePageAccessId } = usePageAccessContext();
  const { linkProps, filterParams } = obRouteContext || {};
  const { filters, tab } = linkProps || {};
  const defaultYear = createDate().subtract(5, 'year').format(DATE_FORMATS.YEAR);
  const [sinceYear, setSinceYear] = useState(() => filters?.[FILTER_TYPES.SINCE_YEAR]?.[0]?.id || defaultYear);
  const [countries, setCountries] = useState(null);
  const isDesiredRole = [tab, activeTab].includes(CASTING_RESULT_TYPES_VALUES.DESIRED_ROLE);
  const isRoleDebut = [tab, activeTab].includes(CASTING_RESULT_TYPES_VALUES.ROLE_DEBUT);
  const parsedQuery = useMemo(() => getBooleanSearchQuery(filters?.expression), [filters?.expression]);
  const defaultQuery = useMemo(() => {
    if (parsedQuery?.length > 0) {
      return parsedQuery;
    }

    return [DEFAULT_BOOLEAN_QUERY];
  }, [parsedQuery]);

  const [booleanQuery, setBooleanQuery] = useState(defaultQuery);
  const [showAddAnotherRole, setShowAddAnotherRole] = useState(booleanQuery?.length > 1);
  const hasMultipleRows = booleanQuery.length > 1 || isRoleDebut || isDesiredRole;
  const workId = booleanQuery?.[0]?.value?.[URL_SLUG_TYPES.WORK];
  const roleDebutWorkRoleId = booleanQuery?.[booleanQuery.length - 1]?.value?.[URL_SLUG_TYPES.WORK_ROLE];
  const selectedTab = CASTING_TOOL_TABS.find(item => item.key === activeTab || item.key === tab);

  const onChangeHandler = useCallback(
    (key, value, index = 0) => {
      if (key === FILTER_TYPES.SINCE_YEAR) {
        setSinceYear(value);
        setCountries([]);
      } else if (key === FILTER_TYPES.COUNTRY) {
        setCountries(value);
      } else {
        const tempData = [...booleanQuery];
        tempData[index] = {
          type: BOOLEAN_SEARCH_PARTS.QUERY,
          value: {
            ...tempData[index]?.value,
            [key]: value,
            ...(key === CASTING_TOOL_SLUGS.WORK && {
              [CASTING_TOOL_SLUGS.WORK_ROLE]: null,
            }),
          },
        };
        setBooleanQuery(tempData);

        if (key === URL_SLUG_TYPES.WORK) {
          const filterDetails = generateFilterParams({
            booleanQuery: tempData,
            sinceYear,
            countryIds: countries,
            isRoleDebut,
          });
          applyFilters({
            baseRoute: ROUTE_RESERVED_KEYWORDS.casting,
            filters: {
              ...filters,
              ...filterDetails,
            },
            tab: selectedTab?.key,
          });
          setShowAddAnotherRole(true);
        }

        if (isRoleDebut && key === URL_SLUG_TYPES.WORK_ROLE && index === 0) {
          const filterDetails = generateFilterParams({
            booleanQuery: tempData,
            sinceYear,
            countryIds: countries,
            isRoleDebut,
          });
          applyFilters({
            baseRoute: ROUTE_RESERVED_KEYWORDS.casting,
            filters: {
              ...filters,
              ...filterDetails,
            },
            tab: selectedTab?.key,
          });
        }
      }
    },
    [applyFilters, booleanQuery, countries, filters, isRoleDebut, sinceYear, selectedTab?.key],
  );

  const onDeleteHandler = useCallback(
    index => {
      const tempQuery = [...booleanQuery]?.filter((_, i) => i !== index && i !== index - 1);
      setBooleanQuery(tempQuery);

      if (booleanQuery?.[index]?.value?.[URL_SLUG_TYPES.WORK]) {
        const filterDetails = generateFilterParams({
          booleanQuery: tempQuery,
          sinceYear,
          countryIds: countries,
          isRoleDebut,
        });

        applyFilters({
          baseRoute: ROUTE_RESERVED_KEYWORDS.casting,
          filters: {
            ...filters,
            ...filterDetails,
          },
          tab: selectedTab?.key,
        });
      }
    },
    [booleanQuery, sinceYear, countries, isRoleDebut, applyFilters, filters, selectedTab],
  );

  const onSearch = useCallback(() => {
    if (!activePageAccessId) {
      showPageAccessPortalForCasting();
      return;
    }

    const cleanedQuery = cleanBooleanQuery(booleanQuery);
    const filterDetails = generateFilterParams({
      booleanQuery: cleanedQuery,
      sinceYear,
      countryIds: countries,
      isRoleDebut,
    });
    track.click(trackSearchGo(filterDetails), GOOGLE_OLD_TRACKING_SERVICES);
    applyFilters({
      baseRoute: ROUTE_RESERVED_KEYWORDS.casting,
      filters: {
        ...filters,
        ...filterDetails,
        ...(isDesiredRole && {
          [FILTER_TYPES.SINCE_YEAR]: null,
          [FILTER_TYPES.COUNTRY]: null,
        }),
      },
      tab: selectedTab?.key,
    });
  }, [
    booleanQuery,
    sinceYear,
    countries,
    isRoleDebut,
    track,
    applyFilters,
    filters,
    isDesiredRole,
    selectedTab,
    showPageAccessPortalForCasting,
    activePageAccessId,
  ]);

  const onClearSearch = useCallback(() => {
    applyFilters({
      baseRoute: ROUTE_RESERVED_KEYWORDS.casting,
      tab: selectedTab?.key,
    });
    setBooleanQuery([DEFAULT_BOOLEAN_QUERY]);
    setSinceYear(defaultYear);
    setCountries(null);
    setShowAddAnotherRole(false);
    track.click(trackClearForm(), GOOGLE_OLD_TRACKING_SERVICES);
  }, [applyFilters, selectedTab, defaultYear, track]);

  useEffect(() => {
    let booleanSearch = [DEFAULT_BOOLEAN_QUERY];

    if (filters?.expression) {
      booleanSearch = getBooleanSearchQuery(filters?.expression);
      setBooleanQuery(booleanSearch);
      setShowAddAnotherRole(booleanSearch?.length > 0);
    }

    setCountries(
      filters?.[FILTER_TYPES.COUNTRY]?.length ? filters?.[FILTER_TYPES.COUNTRY]?.map(country => country?.entity) : [],
    );
  }, [filters]);

  useEffect(() => {
    const handleAddAnotherRole = () => {
      setBooleanQuery(prevQuery => {
        const updatedQuery = [...prevQuery, DEFAULT_OPERATOR, DEFAULT_BOOLEAN_QUERY];
        return updatedQuery;
      });
    };

    window.addEventListener(EVENTS.ADD_ANOTHER_ROLE, handleAddAnotherRole);

    return () => {
      window.removeEventListener(EVENTS.ADD_ANOTHER_ROLE, handleAddAnotherRole);
    };
  }, []);

  useEffect(() => {
    if (defaultQuery?.length) {
      setBooleanQuery(defaultQuery);
    }
  }, [defaultQuery]);

  return (
    <div className={classes.searchRoot}>
      <div className={classes.root}>
        <div className={classes.searchContainer}>
          <div className={classes.searchRows}>
            {booleanQuery.map((boolQuery, index) => {
              const currentWorkId = boolQuery?.value?.[URL_SLUG_TYPES.WORK];
              const currentComposerId = boolQuery?.value?.[URL_SLUG_TYPES.CREATOR];
              const currentRoleId = boolQuery?.value?.[URL_SLUG_TYPES.WORK_ROLE];
              const showRoleField = index > 0 || showAddAnotherRole || isRoleDebut;
              const key = `${boolQuery.type}-${index}-${boolQuery?.value?.[URL_SLUG_TYPES.WORK]}`;
              const showOperator = isRoleDebut || isDesiredRole ? index > 2 : index > 0;
              const showDeleteButton = index > 0;

              if (boolQuery?.type === BOOLEAN_SEARCH_PARTS.OPERATOR) {
                return null;
              }

              return (
                <>
                  <div key={key} className={classes.searchRow}>
                    <div className={classes.rowFields}>
                      {hasMultipleRows && (
                        <div className={classnames({ [classes.labelContainer]: !showOperator })}>
                          {showOperator && (
                            <OperatorBlock
                              value={booleanQuery?.[index - 1]?.value || DEFAULT_OPERATOR}
                              onSelect={option => {
                                const tempQuery = [...booleanQuery];
                                tempQuery[index - 1] = {
                                  ...option,
                                  type: BOOLEAN_SEARCH_PARTS.OPERATOR,
                                };
                                setBooleanQuery(tempQuery);

                                if (tempQuery[index]?.value.work_id) {
                                  const filterDetails = generateFilterParams({
                                    booleanQuery: tempQuery,
                                    sinceYear,
                                    countryIds: countries,
                                    isRoleDebut,
                                  });
                                  applyFilters({
                                    baseRoute: ROUTE_RESERVED_KEYWORDS.casting,
                                    filters: {
                                      ...filters,
                                      ...filterDetails,
                                    },
                                    tab: selectedTab?.key,
                                  });
                                }
                              }}
                              className={classes.operatorBlock}
                              trackingData={{
                                ...trackingData,
                                component: COMPONENTS.SEARCH,
                              }}
                            />
                          )}
                          {(isRoleDebut || isDesiredRole) && (
                            <>
                              {index === 0 && (
                                <Typography size={12} weight="medium" className={classes.labelContainer__title}>
                                  <Trans
                                    i18nKey={isDesiredRole ? `${TP}.FN_WITH_DESIRED_ROLE` : `${TP}.ROLE_DEBUT_FOR`}
                                    ns="NS_CASTING_TOOL"
                                    components={{ b: <b /> }}
                                  />
                                </Typography>
                              )}
                              {index === 2 && (
                                <Typography size={12} weight="medium" className={classes.labelContainer__title}>
                                  {t(`${TP}.SHOULD_HAVE_PERFORMED`)}
                                </Typography>
                              )}
                            </>
                          )}
                        </div>
                      )}
                      <ComposerWorkField
                        workId={currentWorkId}
                        composerId={currentComposerId}
                        onChange={selectedWork => {
                          track.click(trackSearch('work_id', selectedWork), GOOGLE_OLD_TRACKING_SERVICES);
                          onChangeHandler(URL_SLUG_TYPES.WORK, selectedWork?.id, index);
                        }}
                        showComposerField
                        styles={{
                          root: classes.selectField,
                          container: classes.composerWork__container,
                          fieldWrapper: classes.composerWork__fieldWrapper,
                          workFieldWrapper: classes.composerWork__workFieldWrapper,
                        }}
                        trackingData={{
                          category: CATEGORIES.SEARCH,
                          subComponent: SUB_COMPONENTS.COMPOSER,
                          ...trackingData,
                        }}
                        showLabel={index === 0}
                      />
                      {showRoleField && (
                        <div className={classes.castCrew}>
                          <WorkRoleField
                            value={currentRoleId || {}}
                            onChange={selectedRole => {
                              track.click(
                                trackSearch(URL_SLUG_TYPES.WORK_ROLE, selectedRole),
                                GOOGLE_OLD_TRACKING_SERVICES,
                              );
                              onChangeHandler(URL_SLUG_TYPES.WORK_ROLE, selectedRole, index);
                            }}
                            workId={currentWorkId}
                            showWorkComposer={hasMultipleRows}
                            filters={{
                              ...filters,
                              date_from: sinceYear
                                ? createDate(`${sinceYear}`).format(DATE_FORMATS.FULL_DATE)
                                : createDate().subtract(5, 'year').format(DATE_FORMATS.FULL_DATE),
                            }}
                            trackingData={{
                              ...trackingData,
                              category: CATEGORIES.SEARCH,
                              subComponent: SUB_COMPONENTS.WORK_ROLE,
                            }}
                            showLabel={index === 0}
                          />
                        </div>
                      )}
                      {!isDesiredRole && index === 0 && (
                        <YearCountryFilter
                          sinceYear={sinceYear}
                          onChangeHandler={onChangeHandler}
                          workId={workId}
                          countries={countries}
                          filters={filters}
                          filterParams={filterParams}
                          trackingData={trackingData}
                        />
                      )}
                      {showDeleteButton && (
                        <TextButton
                          leftIcon={<SpriteIcon icon="delete" size="16" className={classes.deleteIcon} />}
                          onClick={() => onDeleteHandler(index)}
                          trackingData={{
                            ...trackingData,
                            component: COMPONENTS.SEARCH,
                            subComponent: SUB_COMPONENTS.DELETE_CTA,
                          }}
                        />
                      )}
                    </div>
                  </div>
                  {(isRoleDebut || isDesiredRole) && index === 0 && booleanQuery?.length > 1 && (
                    <div className={classes.divider} />
                  )}
                </>
              );
            })}
          </div>
        </div>
        <Actions
          onClearSearch={onClearSearch}
          onSearch={onSearch}
          workId={workId}
          trackingData={trackingData}
          isRoleDebut={isRoleDebut}
          roleDebutWorkRoleId={roleDebutWorkRoleId}
        />
      </div>
      {!booleanQuery?.[0]?.value?.[URL_SLUG_TYPES.WORK] && (
        <Typography size="12">
          <Trans
            i18nKey={t(selectedTab?.description || CASTING_TOOL_TABS[0].description)}
            ns="NS_APP_GLOBALS"
            components={{ b: <b /> }}
          />
        </Typography>
      )}
    </div>
  );
};

export default DesktopSearch;
